// Mobile parity: gender (required) + notice period + current/expected salary
// in Indian comma format. Sets candidateType: "experienced" on submit.
import { useRef, useState } from "react"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  OnboardingStepShell,
  ContinueButton,
  InlineDropdown,
  OB_COLORS as C,
  BARE_INPUT_STYLE,
} from "./OnboardingStepShell"

const GENDER_OPTIONS = ["Male", "Female", "Non-binary", "Prefer not to say"]
const NOTICE_OPTIONS = [
  "Immediately available", "15 days", "1 month",
  "2 months", "3 months", "More than 3 months",
]

// Indian-style grouping: last 3 digits, then pairs of 2.
function fmtIndian(digits) {
  if (!digits) return ""
  if (digits.length <= 3) return digits
  const last3 = digits.substring(digits.length - 3)
  let rest = digits.substring(0, digits.length - 3)
  const groups = []
  while (rest.length > 2) {
    groups.unshift(rest.substring(rest.length - 2))
    rest = rest.substring(0, rest.length - 2)
  }
  if (rest.length > 0) groups.unshift(rest)
  return `${groups.join(",")},${last3}`
}
const stripFmt = (v) => String(v).replace(/,/g, "")

export default function VoluntaryDisclosuresStep({ onContinue, onBack, step, totalSteps }) {
  const { profile, updateField } = useOnboarding()

  const [gender, setGender] = useState(
    GENDER_OPTIONS.includes(profile?.gender) ? profile.gender : null,
  )
  const [notice, setNotice] = useState(
    NOTICE_OPTIONS.includes(profile?.noticePeriod) ? profile.noticePeriod : null,
  )
  const [currSal, setCurrSal] = useState(
    profile?.currSalary ? fmtIndian(stripFmt(profile.currSalary)) : "",
  )
  const [expSal, setExpSal] = useState(
    profile?.expSalary ? fmtIndian(stripFmt(profile.expSalary)) : "",
  )
  const [openId, setOpenId] = useState(null)
  const bodyRef = useRef(null)

  const canContinue = !!gender

  const submit = () => {
    if (!canContinue) return
    const curr = stripFmt(currSal).trim()
    const exp  = stripFmt(expSal).trim()
    updateField({
      gender,
      noticePeriod: notice || undefined,
      currSalary:   curr || undefined,
      expSalary:    exp  || undefined,
      candidateType: "experienced",
    })
    onContinue()
  }

  return (
    <OnboardingStepShell
      title={"Additional\nDisclosures"}
      subtitle={"Used only where employers are legally required to ask."}
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      bottom={<ContinueButton label="Continue" onClick={submit} disabled={!canContinue} />}
    >
      <div ref={bodyRef} onClick={() => setOpenId(null)} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div onClick={(e) => e.stopPropagation()}>
          <InlineDropdown
            id="gender"
            label="Gender *"
            value={gender}
            hint="Select"
            options={GENDER_OPTIONS}
            openId={openId}
            setOpenId={setOpenId}
            onSelect={(v) => setGender(v)}
          />
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <InlineDropdown
            id="notice"
            label="Notice Period"
            value={notice}
            hint="Select"
            options={NOTICE_OPTIONS}
            openId={openId}
            setOpenId={setOpenId}
            onSelect={(v) => setNotice(v)}
          />
        </div>

        <div style={{
          fontSize: 11, fontWeight: 700, color: C.textMuted,
          letterSpacing: "0.08em", marginTop: 6,
        }}>
          SALARY DISCLOSURE / EXPECTATION
        </div>

        <SalaryField
          label="Current Salary"
          hint="e.g. 12,00,000"
          value={currSal}
          onChange={(v) => setCurrSal(fmtIndian(v.replace(/[^0-9]/g, "")))}
        />
        <SalaryField
          label="Expected Salary"
          hint="e.g. 18,00,000"
          value={expSal}
          onChange={(v) => setExpSal(fmtIndian(v.replace(/[^0-9]/g, "")))}
        />
      </div>
    </OnboardingStepShell>
  )
}

function SalaryField({ label, hint, value, onChange }) {
  return (
    <div style={{
      padding: "10px 16px",
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: C.textMuted,
        letterSpacing: "0.03em", marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 14, color: C.textMuted }}>₹</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder={hint}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...BARE_INPUT_STYLE, fontSize: 14 }}
        />
      </div>
    </div>
  )
}
